import { Bot, Copy, UserRound } from "lucide-react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { ChatMessage } from "../types";
import { formatBytes } from "../lib/ids";

interface MessageBubbleProps {
  message: ChatMessage;
}

const UNSAFE_ORIGIN_CHARACTERS = /[\p{Cc}\p{Cf}]/gu;

export function messageAuthorLabel(message: ChatMessage): string {
  if (message.origin?.kind !== "remote-ledger") {
    return message.role === "assistant" ? "Nexus" : "あなた";
  }
  const deviceName = message.origin.deviceName
    .replace(UNSAFE_ORIGIN_CHARACTERS, "")
    .trim()
    .slice(0, 64) || "参加者";
  const shortNodeId = message.origin.nodeId.replace(/[^A-Za-z0-9_-]/gu, "").slice(0, 8) || "unknown";
  return `${deviceName} · node ${shortNodeId}`;
}

export function MessageBubble({ message }: MessageBubbleProps) {
  const assistant = message.role === "assistant";
  const authorLabel = messageAuthorLabel(message);
  return (
    <article className={`message ${assistant ? "message--assistant" : "message--user"}`}>
      <div className="message-avatar" aria-hidden="true">
        {assistant ? <Bot size={18} /> : <UserRound size={18} />}
      </div>
      <div className="message-body">
        <div className="message-heading">
          <span>{authorLabel}</span>
          {message.state === "streaming" && <span className="streaming-dot">生成中</span>}
        </div>
        {message.attachments?.length ? (
          <div className="message-files">
            {message.attachments.map((attachment) => (
              <span key={attachment.id} className="file-pill file-pill--message">
                {attachment.name} · {formatBytes(attachment.size)}
              </span>
            ))}
          </div>
        ) : null}
        <div className="message-markdown">
          {message.content ? (
            <Markdown
              remarkPlugins={[remarkGfm]}
              components={{
                img: ({ alt }) => (
                  <span className="blocked-image" title="外部画像の自動読込を防止しました">
                    [画像: {alt || "外部画像"}]
                  </span>
                ),
                a: ({ children, href }) => (
                  <a href={href} target="_blank" rel="noreferrer noopener">{children}</a>
                ),
              }}
            >
              {message.content}
            </Markdown>
          ) : (
            <span className="typing-indicator" aria-label="回答を生成中">
              <i />
              <i />
              <i />
            </span>
          )}
        </div>
        <footer className="message-footer">
          {message.metrics && (
            <span>
              {message.metrics.source === "swarm" ? "共有" : "AI"}
              {message.metrics.tokensPerSecond
                ? ` · ${message.metrics.tokensPerSecond.toFixed(1)} tok/s`
                : ""}
              {message.metrics.ragSources?.length
                ? ` · RAG ${message.metrics.ragSources.length}件`
                : ""}
            </span>
          )}
          {message.content && (
            <button
              type="button"
              className="message-action"
              onClick={() => void navigator.clipboard.writeText(message.content)}
              aria-label="回答をコピー"
            >
              <Copy size={14} />
              コピー
            </button>
          )}
        </footer>
      </div>
    </article>
  );
}
